// ─────────────────────────────────────────────────────────────────────────────
// GENERATED FILE — DO NOT EDIT HERE.
//
// Source of truth: invits-app-new/shared/formatEvent.js
// Regenerate:     npm run sync:shared   (from invits-app-new)
//
// Editing this copy reintroduces exactly the app/web divergence that shared/
// exists to eliminate. Change the source, then re-run the sync.
// ─────────────────────────────────────────────────────────────────────────────

// src/features/events/formatEvent.js
//
// Event display formatting, extracted from the screen so it can be shared with
// the web app and unit-tested. Pure — no imports, no hooks.
//
// Behaviour matches the previous inline helpers, with the crash cases fixed:
// the old formatTime did `timeString.split(':')` and `parseInt(...)` with no
// validation, so a malformed or null time column produced "NaN:NaN" or threw
// during render. Everything here degrades to a readable string instead.

/**
 * '2026-10-05' as LOCAL midnight of that calendar day. `new Date()` reads a
 * bare date as UTC midnight - the evening before in the Americas - so US
 * users saw events a day early, and an event today counted as past. (Kept
 * here rather than imported from eventDateTime.js: shared modules stay
 * import-free so their tests can load them.)
 */
function localDate(value) {
  const m = typeof value === 'string' && /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(value);
}

/** @param {string|null|undefined} dateString ISO date or yyyy-mm-dd */
export function formatEventDate(dateString, locale = undefined) {
  if (!dateString) return 'Date not set';
  const date = localDate(dateString);
  if (Number.isNaN(date.getTime())) return 'Date not set';
  return date.toLocaleDateString(locale, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

/** @param {string|null|undefined} timeString 'HH:MM' or 'HH:MM:SS' */
export function formatEventTime(timeString, locale = undefined) {
  if (!timeString || typeof timeString !== 'string') return '';
  const [h, m] = timeString.split(':');
  const hours = Number.parseInt(h, 10);
  const minutes = Number.parseInt(m ?? '0', 10);
  // The old version happily produced "NaN:NaN" here.
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return '';
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return '';

  const d = new Date();
  d.setHours(hours, minutes, 0, 0);
  return d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
}

/**
 * The one-line "when" string shown under the title.
 * Omits separators for absent parts rather than emitting ", " or " - ".
 */
export function formatEventWhen(event, locale = undefined) {
  if (!event) return '';
  const date  = formatEventDate(event.event_date, locale);
  const start = formatEventTime(event.event_time, locale);
  const end   = formatEventTime(event.end_time, locale);

  if (!start) return date;
  // The old template always emitted the " - " when end_time was truthy, which
  // rendered a dangling "7:00 PM - " whenever end_time failed to parse.
  const timeRange = end ? `${start} - ${end}` : start;
  return `${date}, ${timeRange}`;
}

/** Short location label for the meta row. Prefers a name over raw coordinates. */
export function formatEventLocation(event) {
  if (!event) return '';
  return event.location_name || event.venue_name || event.city || '';
}

/**
 * Counts by RSVP status. Tolerates a null list, which the old inline
 * `rsvps.filter(...)` did not.
 */
export function countRsvps(rsvps) {
  const list = Array.isArray(rsvps) ? rsvps : [];
  const counts = { going: 0, maybe: 0, not_going: 0, total: list.length };
  for (const r of list) {
    if (r?.status && counts[r.status] !== undefined) counts[r.status] += 1;
  }
  return counts;
}
